import type { Scenario, ScenarioContent, KalturaAgentBlock } from "@/lib/powtoon-be/types";
import type { ScenarioAdapter, ScenarioView, ScenarioFormState } from "../types";
import { getMode } from "@/lib/scenarios/interactionModes";
import { composeBlockPrompt } from "../composePrompt";
import { strippedForeignFields } from "../spec";

export const kalturaAdapter: ScenarioAdapter = {
  toView(scenario: Scenario): ScenarioView {
    const c = scenario.content ?? {};
    const block = c.kaltura;
    return {
      provider: "kaltura",
      name: scenario.name,
      description: scenario.description,
      thumbnailUrl: scenario.thumb_url || undefined,
      systemPrompt: composeBlockPrompt(block),
      openingText: block?.opening_text,
      faceId: block?.visual_id,
      voiceId: block?.voice_id,
      language: block?.language,
      inputs: block?.inputs ?? [],
    };
  },
  deriveThumbnail(scenario) {
    return scenario.thumb_url || undefined;
  },
  toFormState(scenario: Scenario): Partial<ScenarioFormState> {
    const block = scenario.content?.kaltura;
    return {
      avatarId: block?.visual_id ?? "",
      voiceId: block?.voice_id ?? "",
      language: block?.language ?? "",
      persona: block?.persona ?? "",
      knowledge: block?.knowledge ?? block?.system_prompt ?? "",
      instructions: block?.instructions ?? "",
      openingText: block?.opening_text ?? "",
      mode: block?.interaction_mode ?? "conversational",
      kalturaKnowledgeId: block?.knowledge_id,
      kalturaKnowledgeSourceKeys: block?.knowledge_source_keys ?? [],
      kalturaKnowledgeSources: block?.knowledge_sources ?? [],
      kalturaKnowledgeSyncedAt: block?.knowledge_synced_at,
      kalturaWebSearch: block?.web_search_enabled ?? false,
      kalturaDisableScreenShare: block?.disable_screen_sharing ?? false,
      kalturaPresentDocument: block?.present_document ?? false,
    };
  },
  collectSaveMeta(scenario) {
    const block = scenario?.content?.kaltura;
    return {
      initialAgentId: block?.agent_id,
      initialQuestionnaireIds: block?.questionnaire_ids ?? [],
      initialRoleplayIds: block?.roleplay_ids ?? [],
    };
  },
  toContent(state: ScenarioFormState): ScenarioContent {
    const prev = state.previous ?? {};
    const prevBlock = prev.kaltura;
    const modeMeta = getMode(state.mode);
    const nextBlock: KalturaAgentBlock = {
      
      agent_id: prevBlock?.agent_id,
      intellect_id: prevBlock?.intellect_id,
      app_guid: prevBlock?.app_guid,
      // A scenario keeps its avatar for life: `syncKalturaAgent` updates this
      // pair in place when the face/voice selection changes, so we always
      // carry the id forward and never orphan a catalog entry.
      avatar_id: prevBlock?.avatar_id,
      visual_id: state.avatarId || undefined,
      voice_id: state.voiceId || undefined,
      system_prompt: state.generatedPrompt,
      persona: modeMeta.showPersona ? state.persona : "",
      knowledge: state.knowledge,
      instructions: modeMeta.showInstructions ? state.instructions : "",
      opening_text: state.openingText,
      language: state.language || undefined,
      // Mode + questionnaire versioning live on the block for Kaltura (no
      // context block). `questionnaire_ids` is refreshed by `saveScenario`.
      interaction_mode: state.mode,
      questionnaire_ids: prevBlock?.questionnaire_ids,
      roleplay_ids: prevBlock?.roleplay_ids,
      roleplay_scenario_id:
        state.mode === "role_play" ? prevBlock?.roleplay_scenario_id : undefined,
      // Knowledge record state — edited in the form, attached on save.
      knowledge_id: state.kalturaKnowledgeId ?? prevBlock?.knowledge_id,
      knowledge_source_keys: state.kalturaKnowledgeSourceKeys ?? prevBlock?.knowledge_source_keys,
      knowledge_sources: state.kalturaKnowledgeSources ?? prevBlock?.knowledge_sources,
      knowledge_synced_at: state.kalturaKnowledgeSyncedAt ?? prevBlock?.knowledge_synced_at,
      // Conversational-only capabilities. Persisted regardless of mode so a
      // mode round-trip doesn't lose the trainer's choice; the save pipeline
      // forces them off upstream while the mode isn't conversational.
      web_search_enabled: state.kalturaWebSearch ?? prevBlock?.web_search_enabled ?? false,
      disable_screen_sharing:
        state.kalturaDisableScreenShare ?? prevBlock?.disable_screen_sharing ?? false,
      // Screen sharing is on by default for Kaltura; the trainer opts out.
      screen_share_enabled: !(
        state.kalturaDisableScreenShare ??
        prevBlock?.disable_screen_sharing ??
        false
      ),
      present_document: state.kalturaPresentDocument ?? prevBlock?.present_document ?? false,
      // Minted by the save pipeline; carried through so a form round-trip can't
      // drop the ids and orphan the tools upstream.
      presentation_tool_ids: prevBlock?.presentation_tool_ids ?? [],
      // Placeholder — saveScenario overwrites with post-upload finals.
      inputs: state.initialInputs,
    };
    return {
      ...prev,
      provider: "kaltura",
      // Clears every block and top-level field owned by the other providers
      // (including top-level `language` — Kaltura keeps it on its own block),
      // derived from the provider spec registry.
      ...strippedForeignFields("kaltura"),
      publish_mode: state.publishMode,
      auto_start: state.autoStart,
      cta_type: state.ctaType,
      cta_url: state.ctaType === "sales" ? state.ctaUrl.trim() : "",
      redirect_url: state.redirectUrl.trim(),
      display: state.display,
      kaltura: nextBlock,
    };
  },
};
